/**
 * Map Research — tile source catalog.
 *
 * Basemaps come from OneMap (Singapore Land Authority raster tiles, XYZ scheme,
 * zoom 11–19 within SG bounds) plus Esri World Imagery for a satellite view.
 * Master Plan land-use overlays are served by URA's public ArcGIS tile caches
 * (Web Mercator, standard {z}/{y}/{x} scheme) — the same services URA Space uses.
 */

export interface BasemapOption {
  id: string;
  label: string;
  url: string;
  attribution: string;
  /** Swatch colors for the layer-picker preview chip. */
  preview: [string, string];
  /** True when the tiles are dark, so map chrome can adapt. */
  dark?: boolean;
}

export interface MasterPlanOption {
  id: string;
  /** Chip label, mirrors URA Space (Latest* = MP2025 with approved amendments). */
  label: string;
  url: string;
  description: string;
}

const ONEMAP_ATTRIBUTION =
  '<a href="https://www.onemap.gov.sg/" target="_blank" rel="noopener">OneMap</a> &copy; Singapore Land Authority';

export const BASEMAPS: BasemapOption[] = [
  {
    id: 'night',
    label: 'Night',
    url: 'https://www.onemap.gov.sg/maps/tiles/Night/{z}/{x}/{y}.png',
    attribution: ONEMAP_ATTRIBUTION,
    preview: ['#0f172a', '#334155'],
    dark: true,
  },
  {
    id: 'default',
    label: 'Day',
    url: 'https://www.onemap.gov.sg/maps/tiles/Default/{z}/{x}/{y}.png',
    attribution: ONEMAP_ATTRIBUTION,
    preview: ['#e2e8f0', '#bfdbfe'],
  },
  {
    id: 'grey',
    label: 'Grey',
    url: 'https://www.onemap.gov.sg/maps/tiles/Grey/{z}/{x}/{y}.png',
    attribution: ONEMAP_ATTRIBUTION,
    preview: ['#e5e7eb', '#9ca3af'],
  },
  {
    id: 'original',
    label: 'Classic',
    url: 'https://www.onemap.gov.sg/maps/tiles/Original/{z}/{x}/{y}.png',
    attribution: ONEMAP_ATTRIBUTION,
    preview: ['#fef3c7', '#a7f3d0'],
  },
  {
    id: 'landlot',
    label: 'Land Lot',
    url: 'https://www.onemap.gov.sg/maps/tiles/LandLot/{z}/{x}/{y}.png',
    attribution: ONEMAP_ATTRIBUTION,
    preview: ['#f8fafc', '#fca5a5'],
  },
  {
    id: 'satellite',
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Imagery &copy; Esri &amp; contributors',
    preview: ['#14532d', '#365314'],
    dark: true,
  },
];

const URA_TILE = (service: string) =>
  `https://maps.ura.gov.sg/arcgis/rest/services/${service}/MapServer/tile/{z}/{y}/{x}`;

export const URA_ATTRIBUTION =
  'Master Plan &copy; <a href="https://www.ura.gov.sg/maps/" target="_blank" rel="noopener">Urban Redevelopment Authority</a>';

export const MASTER_PLANS: MasterPlanOption[] = [
  {
    id: 'mp-latest',
    label: 'Latest*',
    url: URA_TILE('MP25/Updated_Landuse_gaz'),
    description: 'Master Plan 2025 with Approved Amendments Incorporated',
  },
  {
    id: 'mp2025',
    label: '2025',
    url: URA_TILE('MP25/Landuse_gaz'),
    description: 'Master Plan 2025 (gazetted)',
  },
  {
    id: 'mp2019',
    label: '2019',
    url: URA_TILE('MP19/Landuse_gaz'),
    description: 'Master Plan 2019 (gazetted)',
  },
  {
    id: 'mp2014',
    label: '2014',
    url: URA_TILE('maps2/MP14_Land_Use_gaz_maps'),
    description: 'Master Plan 2014 (gazetted)',
  },
  {
    id: 'mp2008',
    label: '2008',
    url: URA_TILE('maps2/MP2008'),
    description: 'Master Plan 2008',
  },
  {
    id: 'mp2003',
    label: '2003',
    url: URA_TILE('maps2/MP2003_raster'),
    description: 'Master Plan 2003',
  },
];

export const SG_CENTER: [number, number] = [1.3521, 103.8198];
export const SG_MIN_ZOOM = 11;
export const SG_MAX_ZOOM = 19;
/** OneMap raster coverage bounds, padded slightly so panning never hits a wall abruptly. */
export const SG_MAX_BOUNDS: [[number, number], [number, number]] = [
  [1.09, 103.38],
  [1.63, 104.24],
];

export const DEFAULT_RADIUS_KM = 5;
export const RADIUS_MIN_KM = 0.5;
export const RADIUS_MAX_KM = 10;
/** Scan-radius slider granularity, and the narrowest Super Ratio ring band. */
export const RADIUS_STEP_KM = 0.5;
/** Default master-plan flash cadence (ms between plan swaps). */
export const FLASH_INTERVAL_MS = 500;
/** Slowest flash cadence — left end of the Flash Speed slider. */
export const FLASH_INTERVAL_MAX_MS = 1500;
/** Fastest flash cadence — right end of the Flash Speed slider. */
export const FLASH_INTERVAL_MIN_MS = 200;
